/**
 * /channels/:platform — the Channel Deep-dive drawn natively from Supabase
 * (spec 2026-10-14-native-channel-deep-dive-design). Composition only: reads in
 * useChannelData, numbers in buildChannelView, boxes in ChannelGrid; every
 * filter lives in the URL through useDashboardFilters (R19). /channels opens the
 * first connected channel, and an unknown platform goes back there (R6). The
 * Export menu (PDF, CSV) and the chat read one page input, built from the view
 * the grid draws (channelPageInputOf) and told only once the page settled.
 *
 * @module pages/ChannelDeepDive
 */
import React, { useLayoutEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useParams, useSearchParams } from 'react-router-dom';
import { useDashboardFilters } from '@/hooks/useDashboardFilters';
import { useChannelConnections, useChannelData, type ChannelData } from '@/hooks/useChannelData';
import { useChannelPageScope, useScopeArrival, type PageScope } from '@/hooks/useChannelPageScope';
import { useHeldChatContext } from '@/hooks/useHeldChatContext';
import type { ChannelFreshnessState } from '@/hooks/useLookerChannelFreshness';
import { YDMPageHero } from '@/components/brand';
import { DataStatusPill } from '@/components/looker/DataStatusPill';
import { OverviewBox } from '@/components/overview/OverviewBox';
import { PrintHeader } from '@/components/overview/PrintHeader';
import { ChannelFilterBar } from '@/components/channels/ChannelFilterBar';
import { ChannelTabs } from '@/components/channels/ChannelTabs';
import { ChannelGrid } from '@/components/channels/ChannelGrid';
import { linkSearchOf, parseShared, readFilterMemory, scopeAllAsked } from '@/services/dashboardFilters';
import { addDays, bangkokToday } from '@/services/looker/lookerDateRange';
import type { BoxState, OverviewWindows } from '@/services/overview/overviewTypes';
import { buildChannelView, connectedOf, defaultChannelOf, ga4TabShownOf } from '@/services/channels/channelView';
import { isChannelPlatform, type ChannelPlatform, type ChannelView } from '@/services/channels/channelTypes';
import MediaChatBubble from '@/components/MediaChatBubble';
import { ExportMenu } from '@/components/looker/ExportMenu';
import { CHANNEL_ADVISOR } from '@/config/agentProfiles';
import { PRINT_FRAME_WIDTH_PX, usePrintFrame } from '@/hooks/usePrintFrame';
import { csvLabelsFrom, type DashboardCsvLabels } from '@/services/looker/lookerCsv';
import { buildChannelChatContext } from '@/services/channels/channelChatContext';
import { channelPageSettled, downloadChannelCsv } from '@/services/channels/channelCsv';
import { channelPageInputOf, type ChannelPageInput } from '@/services/channels/channelTables';
import { CHANNEL_LABEL, type TFn } from '@/services/channels/channelFormat';

const NEEDS_WORKSPACE: BoxState<never> = { status: 'needsWorkspace' };
const BANNER_CLASS = 'rounded-lg bg-primary-500 px-3 py-2 text-sm font-semibold text-ink-950';
/** Black on the yellow banner: the brand yellow is a ground for black, never a text colour on white. */
const SHOW_ALL_CLASS = 'print:hidden rounded-md bg-ink-950 px-3 py-1 text-xs font-semibold text-primary-500 hover:bg-ink-800 '
  + 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink-950 focus-visible:ring-offset-2 focus-visible:ring-offset-primary-500';

const periodLineOf = (w: OverviewWindows | null, t: TFn): string | null =>
  (w ? t('looker.periodLine', { start: w.current.start, end: w.current.end, prevStart: w.previous.start, prevEnd: w.previous.end }) : null);

/**
 * The pill's date, for the ONE channel this page shows (R32): an ad channel's
 * own data-through once the statuses were read (D1's newest-of-any rule would
 * date a dormant tab with another channel's day), else the newest facts date.
 * GA4 always dates its own facts, since freshnessOf drops analytics (R28).
 */
const dataDateOf = (platform: ChannelPlatform, freshness: ChannelFreshnessState, view: ChannelView | null): string | null => {
  const facts = view?.latestFactDate ?? null;
  if (platform === 'analytics' || freshness.status !== 'ready') return facts;
  return freshness.channels.find((c) => c.platform === platform)?.dataThrough ?? facts;
};

// ------------------------------------------------------------------ redirects

/**
 * What a redirect keeps: the shared keys of this URL, else of the memory (R24:
 * the D2-only keys belong to one channel), and scope=all (I2), which only the
 * channel page applies. Not useDashboardFilters: its mount normalisation is a
 * replace to "?…" on the CURRENT path, React runs it after <Navigate>'s own
 * effect (a child's runs first), and it would win; <Navigate> never fires
 * again (same target), so the page would stay where it was.
 */
const useSharedSearch = (): string => {
  const [params] = useSearchParams();
  return linkSearchOf(parseShared(params, readFilterMemory(), bangkokToday()), scopeAllAsked(params));
};

const Loading: React.FC = () => {
  const { t } = useTranslation();
  return <p className="text-sm text-neutral-700">{t('looker.loading')}</p>;
};

/** /channels: the first connected ad channel in tab order; Facebook when none is, the read failed, or in all workspaces (R6). */
const IndexRedirect: React.FC<{ page: PageScope }> = ({ page }) => {
  const search = useSharedSearch();
  // Statuses are workspace-bound: all workspaces read none, and neither does a scope still waiting (I2).
  const connections = useChannelConnections(page.scope && !page.showAll ? page.workspaceId : null, 0);
  // No scope = the sidebar workspace is still loading; null from defaultChannelOf = still reading.
  const target = page.scope ? defaultChannelOf(connections, page.showAll) : null;
  return target ? <Navigate replace to={`/channels/${target}?${search}`} /> : <Loading />;
};

/** An unknown platform in the path goes back to /channels (spec §3.1). */
const UnknownRedirect: React.FC = () => {
  const search = useSharedSearch();
  return <Navigate replace to={`/channels?${search}`} />;
};

// ------------------------------------------------------------------ the page

interface PageState {
  filters: ReturnType<typeof useDashboardFilters>;
  data: ChannelData;
  view: ChannelView | null;
  ga4Shown: boolean;
  /** GA4 in all workspaces: never read, as summing different websites' sessions means nothing (R6). */
  blocked: boolean;
}

/** Only the scope, the channel and the period reach a read; every other filter rebuilds the view (§2.2). */
const useChannelPage = (platform: ChannelPlatform, page: PageScope): PageState => {
  const filters = useDashboardFilters();
  const { shared, channel } = filters;
  const blocked = platform === 'analytics' && page.showAll;
  const data = useChannelData(blocked ? null : page.scope, platform, shared.period);
  const connected = connectedOf(platform, data.connections, data.entities, page.showAll);
  const yesterday = addDays(bangkokToday(), -1);
  // Keyed by value, as D1 keys its channel selection. `channel` is a new object on every search change, a
  // breakdown-tab click included, and the builder reads no tab (ChannelGrid draws the open one; a test in
  // ChannelDeepDiveGuards builds with two), so a tab click must not rebuild the view: the tab is fixed here.
  const typesKey = channel.types.join(',');
  const campaignsKey = channel.campaigns.join(',');
  const types = useMemo(() => (typesKey ? typesKey.split(',') : []), [typesKey]);
  const campaigns = useMemo(() => (campaignsKey ? campaignsKey.split(',') : []), [campaignsKey]);
  const { active } = channel;
  const view = useMemo(() => (data.windows
    ? buildChannelView({
      platform, windows: data.windows, facts: data.facts, entities: data.entities, ads: data.ads,
      adSpend: data.adSpend, connected, yesterday, workspaceNames: page.workspaceNames, allWorkspaces: page.showAll,
      filters: { objective: shared.objective, currency: shared.currency, types, campaigns, active, tab: 'campaign' },
    })
    : null), [platform, data, connected, yesterday, page.workspaceNames, page.showAll, shared.objective, shared.currency, types, campaigns, active]);
  return { filters, data, view, ga4Shown: ga4TabShownOf(data.connections, page.showAll), blocked };
};

/** What the Export menu and the chat need (spec §6): the page input, built once per change of what it shows. */
interface PageExport {
  /** null = nothing to write or tell: no scope yet (every read is idle), no window, no view, or GA4 in all workspaces. */
  input: ChannelPageInput | null;
  labels: DashboardCsvLabels;
  /** Every shown box and the sync line are read: a file or a chat before that would say "loading" (D1's pageSettled). */
  settled: boolean;
  printFrame: { framing: boolean; start: () => void };
}

const usePageExport = (platform: ChannelPlatform, page: PageScope, state: PageState): PageExport => {
  const { t } = useTranslation();
  const printFrame = usePrintFrame();
  const labels = useMemo(() => csvLabelsFrom(t), [t]);
  const { data, view, blocked, filters: { shared } } = state;
  // No scope = the page draws no grid and every read is idle: its view is all "loading", so nothing to export or tell.
  const input = useMemo(() => (blocked || !page.scope || !data.windows || !view
    ? null
    : channelPageInputOf({
      view, platform, windows: data.windows, allWorkspaces: page.showAll, workspaceName: page.workspaceName,
      latestDataDate: dataDateOf(platform, data.freshness, view), objective: shared.objective,
      freshness: data.freshness, t,
    })), [blocked, page.scope, data.windows, data.freshness, view, platform, page.showAll, page.workspaceName, shared.objective, t]);
  const settled = input !== null && view !== null && channelPageSettled(view) && data.freshness.status !== 'loading';
  return { input, labels, settled, printFrame };
};

const AllWorkspacesToggle: React.FC<{ checked: boolean; onChange: (on: boolean) => void }> = ({ checked, onChange }) => {
  const { t } = useTranslation();
  return (
    <label className="inline-flex items-center gap-2 text-sm font-medium text-ink-900">
      <input
        type="checkbox"
        className="h-4 w-4 rounded border-neutral-400 accent-ink-950"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      {t('looker.viewAllWorkspaces')}
    </label>
  );
};

interface PartProps {
  platform: ChannelPlatform;
  page: PageScope;
  state: PageState;
}

/** Rows H and F (spec §3.2), as D1's control band, with the Export menu (spec §6). Every filter writes the URL (R19). */
const ControlBand: React.FC<PartProps & { exporter: PageExport }> = ({ platform, page, state, exporter }) => {
  const { t } = useTranslation();
  const { filters: { shared, setShared, setChannel }, data, view } = state;
  const periodLine = periodLineOf(data.windows, t);
  return (
    <section data-channel-controls className="print:hidden space-y-4 rounded-2xl border border-neutral-200 border-l-[5px] border-l-primary-500 bg-white px-5 py-4 shadow-sm">
      <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3">
        {/* GA4 has no type or campaign control (R15); the picks shown are the ones that apply (R11). */}
        <ChannelFilterBar
          token={shared.period} onToken={(period) => setShared({ period })}
          objective={shared.objective} onObjective={(objective) => setShared({ objective })}
          money={view?.money ?? null} onCurrency={(currency) => setShared({ currency })}
          options={platform === 'analytics' || !view ? null : view.options}
          types={view?.applied.types ?? []} onTypes={(types) => setChannel({ types })}
          campaigns={view?.applied.campaigns ?? []} onCampaigns={(campaigns) => setChannel({ campaigns })}
          disabled={data.facts.status === 'loading'}
        />
        <div className="flex flex-wrap items-center gap-3">
          <DataStatusPill latestDataDate={dataDateOf(platform, data.freshness, view)} state={data.freshness} />
          {/* The cut is the ad platforms' attribution lag; GA4 counts its whole window (R15). */}
          {data.windows?.cutApplied && platform !== 'analytics' && (
            <span className="text-xs font-medium text-neutral-800">{t('looker.dataStatus.unsettled')}</span>
          )}
          <ExportMenu
            pdfDisabled={exporter.input === null || data.facts.status === 'loading' || exporter.printFrame.framing}
            onPdf={exporter.printFrame.start}
            csvDisabled={!exporter.settled}
            onCsv={() => exporter.input && downloadChannelCsv(exporter.input, exporter.labels, t)}
          />
          {page.isMaster && <AllWorkspacesToggle checked={page.viewAll} onChange={page.setViewAll} />}
        </div>
      </div>
      {periodLine && <p className="text-xs text-neutral-700">{periodLine}</p>}
      {page.showAll && <p role="status" className={BANNER_CLASS}>{t('looker.allWorkspacesBanner')}</p>}
    </section>
  );
};

/**
 * "กรองเฉพาะ N แคมเปญ", under the control band and outside its print:hidden, so a filtered page says so on
 * paper too. "ดูทั้งหมด" (owner, 2026-10-15) clears the type and the campaign in one click: the way back to the
 * whole channel from a Top 5 link's campaign without opening either picker.
 */
const FilterBanner: React.FC<{ filtered: number | null; onShowAll: () => void }> = ({ filtered, onShowAll }) => {
  const { t } = useTranslation();
  if (filtered === null) return null;
  return (
    <div className={`${BANNER_CLASS} flex flex-wrap items-center justify-between gap-2`}>
      <p role="status">{t('channels.filters.banner', { n: filtered })}</p>
      <button type="button" className={SHOW_ALL_CLASS} onClick={onShowAll}>{t('channels.filters.showAll')}</button>
    </div>
  );
};

/** Rows 2–5, E or the GA4 box, once the page has a scope and a window. */
const ChannelBody: React.FC<PartProps> = ({ platform, page, state }) => {
  const { t } = useTranslation();
  const { filters: { channel, setChannel }, data, view, blocked } = state;
  if (blocked) {
    return <OverviewBox id="ga4" title={t('channels.box.ga4')} state={NEEDS_WORKSPACE}>{() => null}</OverviewBox>;
  }
  if (!page.scope) return <Loading />;
  if (!data.windows || !view) return <p className="text-sm text-neutral-800">{t('looker.badDateRange')}</p>;
  return (
    <ChannelGrid
      platform={platform} view={view} windows={data.windows} tab={channel.tab}
      onTab={(tab) => setChannel({ tab })}
      onActive={(on) => setChannel({ active: on })}
      onRetry={data.retry}
    />
  );
};

/**
 * The bubble in mode 'channels' with CHANNEL_ADVISOR (spec 2026-10-20 page
 * skills §5), told once the page settled. Keyed by its scope, so another
 * channel or workspace starts a new conversation. A filter, the period or
 * Retry keeps it: while the page has no new data yet (its reads are loading,
 * or it refuses the period) the chat holds the last data and takes no new
 * question.
 */
const ChannelChat: React.FC<{ platform: ChannelPlatform; page: PageScope; exporter: PageExport }> = ({ platform, page, exporter }) => {
  const { t } = useTranslation();
  const { input, settled } = exporter;
  const context = useMemo(() => (input && settled ? buildChannelChatContext(input) : null), [input, settled]);
  const scope = `channels:${platform}:${page.showAll ? 'all' : page.workspaceId ?? ''}`;
  const held = useHeldChatContext(context, scope);
  if (!held.context) return null;
  return (
    <div className="print:hidden">
      <MediaChatBubble
        key={scope}
        mode="channels"
        lookerContext={held.context}
        contextPending={held.pending}
        scope={scope}
        title={t('channels.chatTitle')}
        subtitle={t('looker.chatSubtitle')}
        guidedQuestions={CHANNEL_ADVISOR.guidedQuestions}
      />
    </div>
  );
};

const ChannelPage: React.FC<{ platform: ChannelPlatform; page: PageScope }> = ({ platform, page }) => {
  const { t } = useTranslation();
  const state = useChannelPage(platform, page);
  const exporter = usePageExport(platform, page, state);
  // After useDashboardFilters' own URL rewrite, so this replace lands last and carries it (I2).
  useScopeArrival(page, state.filters.shared);
  const periodLine = periodLineOf(state.data.windows, t);
  const filtered = state.view?.filteredCampaigns ?? null;
  const tabsRef = useRef<HTMLDivElement>(null);
  const showAll = (): void => {
    state.filters.setChannel({ types: [], campaigns: [] });
    // The banner leaves with this click, and the focused button with it: focus goes to the open channel's
    // tab just below rather than to <body>, where the next Tab would start again at the top of the page.
    tabsRef.current?.querySelector<HTMLElement>('[aria-current="page"]')?.focus();
  };
  return (
    <div className="space-y-4 p-4 md:p-6 print:p-0">
      <div className="print:hidden">
        <YDMPageHero
          eyebrow={page.showAll ? t('looker.allWorkspaces') : page.workspaceName ?? undefined}
          headline={t('channels.title')} subtitle={t('channels.subtitle')} className="mb-2"
        />
      </div>
      <PrintHeader
        title={`${t('channels.title')} · ${CHANNEL_LABEL[platform]}`}
        scope={page.showAll ? t('looker.allWorkspaces') : page.workspaceName ?? null}
        dataDate={dataDateOf(platform, state.data.freshness, state.view)}
      />
      <ControlBand platform={platform} page={page} state={state} exporter={exporter} />
      <FilterBanner filtered={filtered} onShowAll={showAll} />
      {periodLine && <p className="hidden text-xs text-neutral-800 print:block">{periodLine}</p>}
      {/* The tab you are on is drawn even before the statuses that would list it arrive, or if they never do.
          The exception is GA4 in all workspaces: there is no GA4 tab there (R6). */}
      <div ref={tabsRef}>
        <ChannelTabs
          platform={platform} search={state.filters.sharedSearch}
          showGa4={state.ga4Shown || (platform === 'analytics' && !page.showAll)}
        />
      </div>
      <div
        className="[-webkit-print-color-adjust:exact] [print-color-adjust:exact]"
        style={exporter.printFrame.framing ? { width: PRINT_FRAME_WIDTH_PX, maxWidth: '100%' } : undefined}
      >
        {/* Landscape A4 for this page only; the rule leaves with the page. */}
        <style>{'@page { size: A4 landscape; margin: 10mm; }'}</style>
        <ChannelBody platform={platform} page={page} state={state} />
      </div>
      <ChannelChat platform={platform} page={page} exporter={exporter} />
    </div>
  );
};

/**
 * The element of both routes. The scope sits above the switch, so the master's
 * all-workspaces view outlives /channels ↔ /channels/:platform (both routes
 * render this one element type, which react-router reconciles, not remounts).
 */
const ChannelDeepDive: React.FC = () => {
  const { platform } = useParams<{ platform?: string }>();
  const page = useChannelPageScope();
  // Opens at the top (owner, 2026-10-15). The window kept Overview's scroll from a Top 5 campaign, and scroll
  // anchoring carried it further down as the boxes loaded, so the filter banner and "ดูทั้งหมด" sat above the
  // fold. A channel tab or a filter re-renders this element without remounting it, so neither scrolls.
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  if (platform === undefined) return <IndexRedirect page={page} />;
  if (!isChannelPlatform(platform)) return <UnknownRedirect />;
  return <ChannelPage platform={platform} page={page} />;
};

export default ChannelDeepDive;
